import * as THREE from 'three';

function LowerEnd({ convRatio, color, drawingBaseDimensions }) {
  const width = convRatio * drawingBaseDimensions.belt_width;
  const windThickness = drawingBaseDimensions.winding_thickness;
  const height = convRatio * windThickness;
  const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
    drawingBaseDimensions.configuration
  )
    ? 1
    : -1;
  const inclination =
    THREE.MathUtils.degToRad(drawingBaseDimensions.slope_angle_le) *
    reverseConfig;
  const inclination_db = inclination / 2;

  const leAngular = drawingBaseDimensions.le_angular;
  const leDoubleBend = drawingBaseDimensions.le_double_bend_value;

  const depth_angular = (convRatio * leAngular) / Math.cos(inclination);
  const depth_db = leDoubleBend
    ? (convRatio * leDoubleBend) / Math.cos(inclination_db)
    : 0;
  const depth_flat = drawingBaseDimensions.l2_value
    ? convRatio * drawingBaseDimensions.l2_value
    : 0;

  const heightDiffLE =
    (Math.tan(inclination) * reverseConfig * leAngular +
      Math.tan(inclination_db) * leDoubleBend * reverseConfig) *
    convRatio;

  const pos_y_ang =
    convRatio * (drawingBaseDimensions.h1 - windThickness / 2) -
    (Math.sin(inclination) * reverseConfig * depth_angular) / 2 +
    heightDiffLE;
  const pos_y_db =
    pos_y_ang -
    (Math.sin(inclination) * depth_angular * reverseConfig) / 2 -
    (Math.sin(inclination_db) * depth_db * reverseConfig) / 2;
  const pos_y_flat =
    pos_y_db - (Math.sin(inclination_db) * reverseConfig * depth_db) / 2;

  const guideHeightOutside =
    convRatio * (drawingBaseDimensions.outside_guide_height + windThickness);
  const guideHeightInside =
    convRatio * (drawingBaseDimensions.inside_guide_height + windThickness);
  const guideThicknessOutside =
    convRatio * drawingBaseDimensions.default_guide_thickness;
  const guideThicknessInside =
    convRatio * drawingBaseDimensions.default_guide_thickness;

  const isOutsideGuide =
    drawingBaseDimensions.outside_guide_type === 'Stainless steel' ||
    drawingBaseDimensions.offset_le_outside > 0;
  const isInsideGuide =
    drawingBaseDimensions.inside_guide_type === 'Stainless steel' ||
    drawingBaseDimensions.offset_le_inside > 0;

  const materialProps = {
    color: color,
    roughness: 0.8,
    metalness: 0.5,
    side: THREE.DoubleSide,
  };
  const materials = [
    new THREE.MeshStandardMaterial(materialProps), // right
    new THREE.MeshStandardMaterial(materialProps), // left
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // top
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // bottom
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // front
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // back
  ];

  return (
    <>
      {depth_angular !== 0 && (
        <>
          <mesh
            position={[
              convRatio * (drawingBaseDimensions.central_diam / 2),
              pos_y_ang,
              ((-convRatio * leAngular) / 2) * reverseConfig,
            ]}
            rotation={[-inclination, 0, 0]}
          >
            <boxGeometry args={[width, height, depth_angular]} />
            {materials.map((material, index) => (
              <primitive
                key={index}
                attach={`material-${index}`}
                object={material}
              />
            ))}
          </mesh>
          {isOutsideGuide && (
            <mesh
              position={[
                convRatio *
                  (drawingBaseDimensions.footprint_diameter / 2 +
                    drawingBaseDimensions.offset_le_outside) +
                  guideThicknessOutside / 2,
                pos_y_ang +
                  guideHeightOutside / 2 -
                  (windThickness / 2) * convRatio,
                ((-convRatio * leAngular) / 2) * reverseConfig,
              ]}
              rotation={[-inclination, 0, 0]}
            >
              <boxGeometry
                args={[
                  guideThicknessOutside,
                  guideHeightOutside,
                  depth_angular,
                ]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
          {isInsideGuide && (
            <mesh
              position={[
                convRatio * drawingBaseDimensions.column_centre_to_inside_le -
                  guideThicknessInside / 2,
                pos_y_ang +
                  guideHeightInside / 2 -
                  (windThickness / 2) * convRatio,
                ((-convRatio * leAngular) / 2) * reverseConfig,
              ]}
              rotation={[-inclination, 0, 0]}
            >
              <boxGeometry
                args={[guideThicknessInside, guideHeightInside, depth_angular]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
        </>
      )}
      {depth_db !== 0 && (
        <>
          <mesh
            position={[
              convRatio * (drawingBaseDimensions.central_diam / 2),
              pos_y_db,
              (-convRatio * leAngular - (convRatio * leDoubleBend) / 2) *
                reverseConfig,
            ]}
            rotation={[-inclination_db, 0, 0]}
          >
            <boxGeometry args={[width, height, depth_db]} />
            {materials.map((material, index) => (
              <primitive
                key={index}
                attach={`material-${index}`}
                object={material}
              />
            ))}
          </mesh>
          {isOutsideGuide && (
            <mesh
              position={[
                convRatio *
                  (drawingBaseDimensions.footprint_diameter / 2 +
                    drawingBaseDimensions.offset_le_outside) +
                  guideThicknessOutside / 2,
                pos_y_db +
                  guideHeightOutside / 2 -
                  (windThickness / 2) * convRatio,
                (-convRatio * leAngular - (convRatio * leDoubleBend) / 2) *
                  reverseConfig,
              ]}
              rotation={[-inclination_db, 0, 0]}
            >
              <boxGeometry
                args={[guideThicknessOutside, guideHeightOutside, depth_db]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
          {isInsideGuide && (
            <mesh
              position={[
                convRatio * drawingBaseDimensions.column_centre_to_inside_le -
                  guideThicknessInside / 2,
                pos_y_db +
                  guideHeightInside / 2 -
                  (windThickness / 2) * convRatio,
                (-convRatio * leAngular - (convRatio * leDoubleBend) / 2) *
                  reverseConfig,
              ]}
              rotation={[-inclination_db, 0, 0]}
            >
              <boxGeometry
                args={[guideThicknessInside, guideHeightInside, depth_db]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
        </>
      )}
      {depth_flat !== 0 && (
        <>
          <mesh
            position={[
              convRatio * (drawingBaseDimensions.central_diam / 2),
              pos_y_flat,
              (-convRatio * leAngular -
                convRatio * leDoubleBend -
                depth_flat / 2) *
                reverseConfig,
            ]}
          >
            <boxGeometry args={[width, height, depth_flat]} />
            {materials.map((material, index) => (
              <primitive
                key={index + 6}
                attach={`material-${index}`}
                object={material}
              />
            ))}
          </mesh>
          {isOutsideGuide && (
            <mesh
              position={[
                convRatio *
                  (drawingBaseDimensions.footprint_diameter / 2 +
                    drawingBaseDimensions.offset_le_outside) +
                  guideThicknessOutside / 2,
                pos_y_flat +
                  guideHeightOutside / 2 -
                  (windThickness / 2) * convRatio,
                (-convRatio * leAngular -
                  convRatio * leDoubleBend -
                  depth_flat / 2) *
                  reverseConfig,
              ]}
            >
              <boxGeometry
                args={[guideThicknessOutside, guideHeightOutside, depth_flat]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
          {isInsideGuide && (
            <mesh
              position={[
                convRatio * drawingBaseDimensions.column_centre_to_inside_le -
                  guideThicknessInside / 2,
                pos_y_flat +
                  guideHeightInside / 2 -
                  (windThickness / 2) * convRatio,
                (-convRatio * leAngular -
                  convRatio * leDoubleBend -
                  depth_flat / 2) *
                  reverseConfig,
              ]}
            >
              <boxGeometry
                args={[guideThicknessInside, guideHeightInside, depth_flat]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
        </>
      )}
    </>
  );
}

export default LowerEnd;
